import { useT } from '@clara/locale'
import { object, AnyObjectSchema, string } from 'yup'

import { OPERATION_COUNTRY } from '@/config/env'
import { validBrPhone, validBrRandomKey, validCPF } from '@/utils/string'

export const getBankInfoSchema = (
  t: ReturnType<typeof useT>['t'],
  brTypeAccount?: 'pix' | 'ted',
) => {
  const schema: { [key: string]: AnyObjectSchema } = {
    MX: object({
      name: string().required(),
      clabe: string()
        .required(t('form.validation.required'))
        .matches(/^[0-9]+$/, t('form.validation.account'))
        .max(18, t('form.validation.account'))
        .min(18, t('form.validation.account')),
      rfc: string()
        .required(t('form.validation.required'))
        .matches(
          /^([A-Za-zÑñ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Za-z\d]{2})([Aa\d])$/,
          t('form.validation.taxPayerId'),
        ),
    }),
    pix: object({
      selectedMethod: string().oneOf(['randomKey', 'phone', 'cpf', 'email']),
      randomKey: string().when('selectedMethod', {
        is: 'randomKey',
        then: (schema) =>
          schema
            .required(t('form.validation.required'))
            .test(
              'is-valid-br-random-key',
              'form.validation.randomKey',
              (value) => validBrRandomKey(value),
            ),
      }),
      phone: string().when('selectedMethod', {
        is: 'phone',
        then: (schema) =>
          schema
            .required(t('form.validation.required'))
            .test('is-valid-br-phone', 'form.validation.phone', (value) =>
              validBrPhone(value),
            ),
      }),
      cpf: string().when('selectedMethod', {
        is: 'cpf',
        then: (schema) =>
          schema
            .required(t('form.validation.required'))
            .test('is-valid-cpf', 'form.validation.pix', (value) =>
              validCPF(value),
            ),
      }),
      email: string().when('selectedMethod', {
        is: 'email',
        then: (schema) =>
          schema
            .email(t('form.validation.email'))
            .required(t('form.validation.required')),
      }),
    }),
    ted: object().shape({
      cpf: string()
        .required(t('form.validation.required'))
        .test('is-valid-cpf', 'form.validation.cpf', (value) =>
          validCPF(value),
        ),
      branchCode: string()
        .transform((value) =>
          isNaN(value) || value === null || value === undefined ? 0 : value,
        )
        .min(1, 'form.validation.branchCode')
        .max(4, 'form.validation.branchCode')
        .required('form.validation.required'),
      accountNumber: string()
        .transform((value) =>
          isNaN(value) || value === null || value === undefined ? 0 : value,
        )
        .min(1, 'form.validation.accountNumber')
        .required('form.validation.required'),
      accountSubType: string().required('form.validation.required'),
      bankKey: string().required('form.validation.required'),
    }),
    CO: object({
      accountNumber: string()
        .required('form.validation.required')
        .min(5)
        .max(20)
        .matches(/^[0-9]{5,20}$/, t('form.validation.accountNumber')),
      accountType: string().required('form.validation.required'),
      identificationType: string().required('form.validation.required'),
      identificationNumber: string()
        .required(t('form.validation.required'))
        .when('identificationType', {
          is: 'PASSPORT',
          then: (schema) =>
            schema.matches(
              /^[A-Z0-9]{6,9}$/,
              t('form.validation.identificationNumber'),
            ),
        })
        .when('identificationType', {
          is: 'CITIZENSHIP_ID',
          then: (schema) =>
            schema
              .min(6)
              .max(10)
              .matches(
                /^[0-9]{6,10}$/,
                t('form.validation.identificationNumber'),
              ),
        })
        .when('identificationType', {
          is: 'FOREIGNER_ID',
          then: (schema) =>
            schema
              .min(6)
              .max(7)
              .matches(
                /^[0-9]{6,7}$/,
                t('form.validation.identificationNumber'),
              ),
        }),
      bankKey: string().required('form.validation.required'),
    }),
  }

  return brTypeAccount ? schema[brTypeAccount] : schema[OPERATION_COUNTRY]
}
